import { useSyncExternalStore } from "react";
import { userStorageKey } from "@medoma/shared-employee/userStorageKey";

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener("storage", callback);
  };
};

/**
 * Persists a value for the signed-in employee, for as long as the tab lives.
 *
 * sessionStorage is scoped to the tab, not to the session: it outlives a sign
 * out and a sign in on that same tab. The key is therefore scoped by user, the
 * way localStorage is, and `clearUserSessionStorage` drops it on sign-out.
 */
export const useSessionStorage = ({
  key,
  userId,
  initialValue = "",
}: {
  key: string;
  userId: string;
  initialValue?: string;
}) => {
  const scopedKey = userStorageKey({ userId, key });

  const storedValue = useSyncExternalStore(
    subscribe,
    () => window.sessionStorage.getItem(scopedKey) ?? initialValue,
  );

  const setValue = (value: string) => {
    window.sessionStorage.setItem(scopedKey, value);
    window.dispatchEvent(new Event("storage"));
  };

  return [storedValue, setValue] as const;
};
